import type { EquipmentPurpose } from '@/types/equipment'
import { useCallback, useMemo, useState } from 'react'
import { useMyList, apiEventToTask } from '@/hooks/useMyList'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import { useEquipment } from '@/hooks/useEquipment'
import { useAuth } from '@/providers/AuthProvider'
import { addDays, daysBetween } from '@/lib/taskDateRange'
import type { UpdateEventPayload } from '@/api/equipmentApi'
import type { MaintenanceTask } from '@/types/maintenanceTask'
import type { ScheduleEvent, ScheduleStatus, Technician } from '@/types/teamSchedule'
import { useReservationsInWindow } from '@/hooks/useProcessSteps'
import { isReservationCard, reservationsToScheduleEvents } from '@/lib/reservationSchedule'

export type WeekDay = { date: Date; iso: string; isToday: boolean }

/** Local (not UTC) ISO date to avoid timezone day-shifts. */
function isoLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Monday of the week containing `ref`. */
function mondayOf(ref: Date): Date {
  const d = new Date(ref)
  const dow = (d.getDay() + 6) % 7 // 0 = Monday
  d.setDate(d.getDate() - dow)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Whole weeks from this week to the week containing `target`.
 *
 * Measured Monday-to-Monday and rounded, so a DST change inside the span (a
 * 23- or 25-hour day) cannot land the board one week off.
 */
export function weekOffsetFor(targetIso: string, today: Date = new Date()): number {
  const target = new Date(`${targetIso}T00:00:00`)
  if (Number.isNaN(target.getTime())) return 0
  const ms = mondayOf(target).getTime() - mondayOf(today).getTime()
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000))
}

/** MaintenanceTask status → the schedule card's status vocabulary. */
function scheduleStatus(task: MaintenanceTask): ScheduleStatus {
  if (task.status === 'completed') return 'done'
  if (task.status === 'started') return 'started'
  if (task.status === 'overdue') return 'overdue'
  return 'scheduled'
}

/**
 * Technician-grouped schedule for a working week (Mon–Fri), backed by the
 * persisted maintenance-events feed (GET /equipment/events) + assignable users.
 * Exposes the real Start/End/Undo mutations and a task resolver so the tab can
 * drive the e-signed execution modals. Rows are keyed by assignee; unassigned
 * events collect under an "Unassigned" row until they're assigned (86ey976q1).
 */
export function useTeamSchedule(
  /** Only equipment of these purposes — the Equipment or the Cleaning page's share (z8nrz7ewt2). */
  purposes?: readonly EquipmentPurpose[],
) {
  const [weekOffset, setWeekOffset] = useState(0)

  const week = useMemo<WeekDay[]>(() => {
    const monday = mondayOf(new Date())
    monday.setDate(monday.getDate() + weekOffset * 7)
    const todayIso = isoLocal(new Date())
    return Array.from({ length: 5 }, (_, i) => {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      const iso = isoLocal(date)
      return { date, iso, isToday: iso === todayIso }
    })
  }, [weekOffset])

  // Feed range spans Mon → Sat (exclusive end) so the whole working week is fetched.
  const range = useMemo(() => {
    const sat = new Date(week[4].date)
    sat.setDate(sat.getDate() + 1)
    return { from: week[0].iso, to: isoLocal(sat) }
  }, [week])

  const feed = useMemo(() => ({ ...range, purposes }), [range, purposes])
  const { events: raw, start, end, undoEnd, updateEvent } = useMyList(feed)
  const { users } = useAssignableUsers()
  const { data: units } = useEquipment(purposes)
  const { email } = useAuth()

  const locationById = useMemo(() => {
    const m = new Map<string, string>()
    for (const u of units ?? []) m.set(u.id, u.location.display)
    return m
  }, [units])

  const dayIndexByIso = useMemo(() => {
    const m = new Map<string, number>()
    week.forEach((d, i) => m.set(d.iso, i))
    return m
  }, [week])

  // Process-step bookings occupy the same machines this board schedules people
  // against, so they belong on it — read-only, since the process step owns their
  // lifecycle. Same week window as the events feed, so the two always agree.
  const { data: reservations } = useReservationsInWindow(range.from, range.to, purposes)

  const technicianIdByEmail = useMemo(() => {
    const m = new Map<string, string>()
    for (const u of users) if (u.email) m.set(u.email.trim().toLowerCase(), u.id)
    return m
  }, [users])

  const reservationEvents = useMemo<ScheduleEvent[]>(
    () =>
      reservationsToScheduleEvents(reservations ?? [], {
        dayIndexByIso,
        technicianIdByEmail,
        locationById,
      }),
    [reservations, dayIndexByIso, technicianIdByEmail, locationById],
  )

  const maintenanceEvents = useMemo<ScheduleEvent[]>(
    () =>
      raw
        .flatMap((ev): (ScheduleEvent | null)[] => {
          // A repeating task is DUE once, at the end of its series, but worked
          // every cadence date in between — so placing it by `dueDate` alone
          // put it on the board in one week out of fifty-two. The feed sends
          // the days inside this window it actually lands on; draw one card per
          // day, each still pointing at the single task underneath.
          const days = ev.occurrenceDates?.length ? ev.occurrenceDates : [ev.dueDate.slice(0, 10)]
          return days.map((day) => {
            const dayOffset = dayIndexByIso.get(day)
            if (dayOffset === undefined) return null // outside Mon–Fri of this week
            return {
              id: ev.occurrenceDates?.length ? `${ev.id}|${day}` : ev.id,
              taskId: ev.id,
              technicianId: ev.assigneeId ?? 'unassigned',
              dayOffset,
              equipmentId: ev.equipment?.label ?? ev.material?.materialName ?? ev.purchaseOrder?.number ?? ev.equipmentId ?? '',
              eventType: ev.eventTypeCode,
              eventClass: ev.taskClass === 'corrective' ? 'corrective' : 'preventive',
              // Nominal 09:00 when the event carries no scheduled window.
              timeStart: ev.plannedStart ? ev.plannedStart.slice(11, 16) : '09:00',
              planHours: ev.planHours ?? 0,
              status: scheduleStatus(apiEventToTask(ev)),
              location: (ev.equipmentId && locationById.get(ev.equipmentId)) || '—',
              sop: ev.sopRef ?? '—',
            }
          })
        })
        .filter((e): e is ScheduleEvent => e !== null),
    [raw, dayIndexByIso, locationById],
  )

  const events = useMemo<ScheduleEvent[]>(
    () => [...maintenanceEvents, ...reservationEvents],
    [maintenanceEvents, reservationEvents],
  )

  // Only technicians who actually have events this week (so the board shows who is
  // working on what). Unassigned tasks are surfaced separately via `hasUnassigned`.
  const technicians = useMemo<Technician[]>(() => {
    const byId = new Map(users.map((u) => [u.id, u]))
    const assignedIds = [
      ...new Set(events.filter((e) => e.technicianId !== 'unassigned').map((e) => e.technicianId)),
    ]
    return assignedIds
      .map((id) => {
        const name = byId.get(id)?.name ?? `Tech ${id.slice(0, 6)}`
        return { id, name, initial: name.charAt(0).toUpperCase() || '?', role: 'Technician' }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [users, events])

  const hasUnassigned = useMemo(() => events.some((e) => e.technicianId === 'unassigned'), [events])

  // Reschedule (dueDate) and/or reassign (assigneeId, '' to unassign) — drag-drop
  // + explicit assign. Stamps the current user as the actor for the audit trail.
  const moveEvent = useCallback(
    (id: string, patch: { dueDate?: string; assigneeId?: string }, fromDayIso?: string) => {
      // A booking has no MaintenanceEvent behind it, so a PATCH would 404 —
      // and rescheduling one here would silently disagree with the process step
      // that actually owns the window. Refused at the hook, not just hidden in
      // the UI, so no future caller can route around it.
      if (isReservationCard(id)) return
      const payload: UpdateEventPayload = { ...patch, actor: email ?? undefined }

      // Dragging ONE card of a repeating task moves the WHOLE series. There is
      // a single task behind every card, so there is a single day-of-week:
      // shift its start date by the days dragged and every occurrence follows.
      // Setting `dueDate` instead would move the END of the series to the day
      // dropped on, collapsing a year of work onto next Tuesday.
      const ev = raw.find((e) => e.id === id)
      if (ev?.repeatCycle && patch.dueDate && fromDayIso) {
        const start = (ev.earlyStart ?? ev.dueDate).slice(0, 10)
        delete payload.dueDate
        payload.earlyStart = addDays(start, daysBetween(fromDayIso, patch.dueDate))
      }
      updateEvent(id, payload)
    },
    [updateEvent, email, raw],
  )

  // Resolve a schedule event id → MaintenanceTask for the execution modals.
  const taskById = useMemo(() => {
    const m = new Map<string, MaintenanceTask>()
    for (const ev of raw) m.set(ev.id, apiEventToTask(ev))
    return m
  }, [raw])

  const weekLabel = useMemo(
    () => week[0].date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
    [week],
  )

  return {
    technicians,
    hasUnassigned,
    assignableUsers: users,
    events,
    week,
    weekLabel,
    weekOffset,
    prevWeek: () => setWeekOffset((w) => w - 1),
    nextWeek: () => setWeekOffset((w) => w + 1),
    goToday: () => setWeekOffset(0),
    /** Jump to the working week containing a chosen date. */
    goToDate: (iso: string) => setWeekOffset(weekOffsetFor(iso)),
    /** The visible week's Mon–Fri bounds, for the date picker's value. */
    weekStartIso: week[0].iso,
    weekEndIso: week[4].iso,
    taskById,
    moveEvent,
    start,
    end,
    undoEnd,
  }
}
